// The `GET /user/` FETCH OWNER, carved from `src/hooks/query/useQueryUser.ts`
// (now a signature-stable delegate). Everything the query IS — the frozen
// `GET_USER` identity, the `skipToken` gate, the staleTime, the
// always-refetch-on-focus rule, the retry predicate and the org-access
// classification — lives here. Everything the query CAUSED stayed app-side and
// arrives through the seam, because `/data` is headless.
//
// This reverses a recorded position rather than filling a gap in one, so the
// reversal is stated instead of implied. The family record reads: "`query`'s
// single implementing symbol is the fetch owner that task 1.4 and the W-series
// query-as-prop inversion put app-side ON PURPOSE, and moving it would falsify
// this family's own Green `verification` assertion that the package never
// fetches." That assertion is what changes. The reasoning under it — the fetch
// owner is a composition of app effects — was re-measured symbol by symbol and
// holds for the EFFECTS, not for the QUERY: every crossing below is an app
// reaction, and once each one is a port there is nothing app-shaped left in the
// fetch. Two records cite this file's app-side identity as their blocking leg,
// which is what makes the trade worth taking.
//
// WHY TWO CROSSING MECHANISMS, since a reader will notice the file uses both.
// A value that is an INVARIANT module-scope reference crosses as a PARAMETER; a
// value that closes over a caller's render state crosses on the CONFIG SEAM.
// The line is the mount-order hazard `onTeamDetailsFetchError` records — a
// `queryFn` belongs to the QUERY, not to an observer, so the fetch adopts the
// options of whichever observer started it, and a port that can differ per
// observer therefore fires by mount order. `fetchCurrentUser` is bound by the
// single app delegate to one module-scope function and cannot differ; the three
// reactions close over `t`, a jotai setter and the router, so they are seam
// members.
//
// WHAT IS NOT HERE, AND IS NOT AN OVERSIGHT: `invalidateCurrentUser`. It is
// composite over this domain's `GET_USER` and COMMERCE's `GET_PRODUCTS` behind
// one try/catch, and workspace is FOUNDATIONAL, so the foreign half is not
// importable. The refusal is measured and recorded at the app declaration.
import { skipToken, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';

import { GET_USER } from '../../contracts/keys';
import type { UserResponse } from '../../contracts/types/user-types';
import { getWorkspaceDataConfig, isTransportError } from '../config';
import type { QueryUserResponse } from '../current-user-context';

const ssoGroupAccessBlockedSchema = z.object({
  detail: z.object({
    type: z.literal('SSOGroupAccessBlockedError'),
  }),
});

/** Check if error response indicates a blocked user (SSO group access blocked) */
export const isOrgAccessRequiredError = (response: {
  status: number;
  data: unknown;
}): boolean =>
  response.status === 403 && ssoGroupAccessBlockedSchema.safeParse(response.data).success;

/**
 * The same classification over the STRUCTURAL transport error, whose `status`
 * is optional where an `AxiosError`'s is not. A missing status took the
 * `403 === undefined` branch and returned false before the carve, so this is
 * the legacy predicate's behaviour restated against a wider input type rather
 * than a second rule — and the public signature above stays exactly as its
 * app-side importers know it.
 */
const isOrgAccessRequiredTransportResponse = (response: {
  status?: number;
  data?: unknown;
}): boolean =>
  response.status !== undefined &&
  isOrgAccessRequiredError({ status: response.status, data: response.data });

/**
 * The `retry` predicate's whole test, as a function over `unknown`.
 *
 * Deliberately not inlined into `retry`: react-query infers the query's
 * `TError` from that callback's parameter, so annotating it `unknown` to let
 * `isTransportError` narrow would silently widen `UseQueryResult`'s error type
 * for all six app call sites. Measured — `pnpm exec tsc --noEmit` failed at
 * every one of them.
 */
const isOrgAccessRequiredTransportError = (error: unknown): boolean =>
  isTransportError(error) &&
  !!error.response &&
  isOrgAccessRequiredTransportResponse(error.response);

/**
 * The app's instrumented `GET /user/` read.
 *
 * A PARAMETER rather than this package's own `data/api#getUser`, for one
 * measured reason. The app wrapper (`@/lib/utils#getUser`) calls that same
 * transport and then fires the browser half of the Meta signup-conversion pair,
 * and its own comment says why it sits there: it is "the single choke point
 * every get-or-create entry point funnels through — the app-boot context query,
 * the onboarding-v2 completion `fetchQuery`, dictation about-you", six further
 * app entry points at this head. Calling the raw transport here would silently
 * drop that event on the app-boot path; re-firing it from the delegate would
 * fork a documented exactly-once guarantee into two implementations.
 */
export type FetchCurrentUser = () => Promise<{ data: UserResponse | undefined }>;

/** The app-resolved values {@link useQueryUserForContext}'s gate is built from. */
export interface CurrentUserContextQueryInput {
  /** See {@link FetchCurrentUser}. */
  fetchCurrentUser: FetchCurrentUser;
  /**
   * `useQueryKindeProperties().data?.region`, resolved app-side — the region
   * lives in an app context this package may not read. Only its presence is
   * read, exactly as the legacy `!!kindeProperties?.region` did.
   */
  kindeRegion: string | undefined;
  /**
   * `isUserQueryBlockedRoute(router.pathname)`, resolved app-side.
   *
   * The pathname had exactly two uses and both are app knowledge: this
   * predicate, which is a set of Next route patterns kept in its own
   * dependency-free app module so `useQueryKindeProperties` can share it
   * without a circular import, and the redirect guard, which is part of the
   * redirect reaction. So the resolved BOOLEAN crosses and the route tree stays
   * app-side — the same shape a resolved feature flag takes.
   */
  isRouteBlocked: boolean;
}

/**
 * The current-user context query — the single observer feeding
 * `CurrentUserProvider`, which every `useQueryUser` / `useUser` read in the app
 * is a projection of.
 *
 * `skipToken` rather than `enabled: false` is load-bearing and preserved
 * verbatim: the two differ in the `fetchStatus`/`isPending` pair consumers read
 * off this query, so swapping them would be a behaviour change wearing a
 * refactor's clothes.
 */
export function useQueryUserForContext<T = QueryUserResponse>(
  { fetchCurrentUser, kindeRegion, isRouteBlocked }: CurrentUserContextQueryInput,
  select?: (data: QueryUserResponse) => T
) {
  const token = getWorkspaceDataConfig().useAuthToken();
  // The OBSERVER's client, not the seam's singleton. They are the same instance
  // in the app, and the legacy body read `useQueryClient()` — a suite that
  // wraps the hook in its own `QueryClientProvider` is the case where they
  // differ, and it is the previous value of THIS query that is being read.
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: [GET_USER],
    initialData: undefined,
    select,
    staleTime: 5 * 60 * 1000, // 5 minutes
    // Always refetch when user returns to the app to ensure consent settings
    // and other critical user data are up to date.
    refetchOnWindowFocus: 'always',
    retry: (failureCount, error) => {
      // Don't retry if the user is blocked by SSO group access
      if (isOrgAccessRequiredTransportError(error)) {
        return false;
      }
      return failureCount < 1;
    },
    queryFn:
      !!token && !!kindeRegion && !isRouteBlocked
        ? async () => {
            try {
              const { data } = await fetchCurrentUser();
              if (!data) {
                throw new Error('Failed to fetch user data');
              }
              const previousData = queryClient.getQueryData<UserResponse>([GET_USER]);

              // The four app effects that ran inline here — three quota toasts,
              // the seen-modal sync, the temp-user clear and the chat migration
              // — in the one order they ran in. See `onCurrentUserFetched` for
              // why a throw from the reaction is NOT swallowed.
              getWorkspaceDataConfig().onCurrentUserFetched?.({
                user: data,
                previousUser: previousData,
              });

              return {
                ...data,
                // TODO: This is a temporary fix to ensure the unlimited quota is in the correct format
                hasUnlimitedQuota: data.quota?.unlimited_quota_until
                  ? Date.parse(data.quota.unlimited_quota_until + 'Z') > Date.now()
                  : false,
              };
            } catch (error) {
              // `isTransportError` is the `instanceof AxiosError` this replaced
              // — axios's own `isAxiosError` marker, which additionally
              // survives a duplicate axios copy where `instanceof` does not.
              if (isTransportError(error)) {
                // !Should handle this logic in query client
                const response = error.response;
                if (!response || !response.status || !response.data) {
                  throw error;
                }

                // Redirect blocked users to organization access required page.
                // The reaction is the app's, and both of its halves are: the
                // "am I already there?" route check, and a HARD
                // `window.location.replace` that exists to cancel all pending
                // Next.js router navigations (e.g. index.tsx -> /scribe) and
                // in-flight requests, preventing race conditions.
                if (isOrgAccessRequiredTransportResponse(response)) {
                  getWorkspaceDataConfig().onOrgAccessRequired?.();
                  throw error;
                }

                getWorkspaceDataConfig().onCurrentUserFetchError?.(error);
                throw error;
              }
              throw error;
            }
          }
        : skipToken,
  });
}
